"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthorizerAuth0 = void 0;
require("source-map-support/register");
const jsonwebtoken_1 = __importDefault(require("jsonwebtoken"));
const jwk_to_pem_1 = __importDefault(require("jwk-to-pem"));
const logger_1 = require("@linq/logger");
const Request_1 = require("./Request");
const RequestUtil_1 = require("./RequestUtil");
const Unauthorized_1 = require("./HttpErrors/Unauthorized");
class AuthorizerAuth0 {
    static async validate(options) {
        const token = RequestUtil_1.RequestUtil.getToken(options.token);
        const validIssuers = options.issuers?.split(',').map((issuer) => `https://${issuer.trim()}/`);
        // decode the base64Url token into an object
        const decodedToken = jsonwebtoken_1.default.decode(token, { complete: true });
        logger_1.Logger.debug('token => ', decodedToken);
        if (!decodedToken || typeof decodedToken === 'string' || !decodedToken.payload || !decodedToken.header?.kid) {
            throw new Error('invalid access token.');
        }
        if (!validIssuers?.includes(decodedToken.payload?.iss)) {
            throw new Error(`invalid token issuer (${decodedToken.payload?.iss}), expected one of ${options.issuers}`);
        }
        if (options.audience && Array.isArray(decodedToken.payload?.aud)) {
            if (!decodedToken.payload?.aud.includes(options.audience)) {
                const altAudienceChecks = options.altAudiences?.map((altAudience) => {
                    logger_1.Logger.debug('altAudience', altAudience);
                    return decodedToken.payload?.aud.includes(altAudience);
                });
                logger_1.Logger.debug('altAudienceChecks', altAudienceChecks);
                if (altAudienceChecks?.includes(true)) {
                    logger_1.Logger.debug('token matched alternate accepted audience');
                }
                else {
                    const audiences = options.altAudiences ? [options.audience, ...options.altAudiences] : [options.audience];
                    throw new Error(`expected ${audiences.join(',')} not found. token contains audience(s) ${decodedToken.payload?.aud}`);
                }
            }
        }
        else if (options.audience && decodedToken.payload?.aud !== options.audience) {
            if (typeof decodedToken.payload.aud === 'string' && options.altAudiences?.includes(decodedToken.payload.aud)) {
                logger_1.Logger.debug('token matched alternate accepted audience');
            }
            else {
                const audiences = options.altAudiences ? [options.audience, ...options.altAudiences] : [options.audience];
                throw new Error(`expected ${audiences.join(',')} not found. token contains audience ${decodedToken.payload?.aud}`);
            }
        }
        let jwk;
        if (options.jwks) {
            jwk = options.jwks.find((key) => {
                return key.kid === decodedToken.header.kid;
            });
        }
        // fallback to fetching key set
        if (!jwk) {
            const request = new Request_1.Request();
            const metadataEndpoint = `${validIssuers?.[0]}.well-known/jwks.json`;
            const response = await request.get(metadataEndpoint);
            // validate the token type in the header matches one of the public keys for the vault app client
            jwk = response.data.keys.find((key) => {
                return key.kid === decodedToken.header.kid;
            });
        }
        if (!jwk) {
            throw new Error('invalid access token signing key.');
        }
        // verify the encrypted signature and expiration to ensure token hasn't been tampered with or expired
        const pem = jwk_to_pem_1.default(jwk);
        jsonwebtoken_1.default.verify(token, pem, { algorithms: ['RS256'] });
        return decodedToken;
    }
    /**
     * validates token by checking issuer, audience, expiration, and encrypted signature
     * @param options - options for validating expected claims in token
     */
    static async validateToken(options) {
        try {
            await this.validate(options);
        }
        catch (err) {
            throw new Unauthorized_1.Unauthorized({ internalError: err });
        }
    }
    /**
     * validates token and generates a default AWS policy that globally
     * applies read, write, and delete access for the API.  If more fine-grained
     * scopes are used such as allowing read access to a subset of endpoints
     * then the policy should be generated by the service.
     * @param options - policy options for token validation and default policy generation
     * @returns AWS IAM access policy for invoking underlying lambda functions
     */
    static async generatePolicy(options) {
        try {
            const decodedToken = await this.validate(options);
            const resourceList = [];
            const scopes = decodedToken.payload.scope?.split(' ') ?? [];
            const region = options.region ?? '*';
            // read permission
            if (options.acceptedClaims.read && scopes.includes(options.acceptedClaims.read)) {
                resourceList.push(`arn:aws:execute-api:${region}:*:*/GET/*`);
            }
            // write permission
            if (options.acceptedClaims.write && scopes.includes(options.acceptedClaims.write)) {
                resourceList.push(`arn:aws:execute-api:${region}:*:*/POST/*`);
                resourceList.push(`arn:aws:execute-api:${region}:*:*/PUT/*`);
                resourceList.push(`arn:aws:execute-api:${region}:*:*/PATCH/*`);
            }
            // delete permission
            if (options.acceptedClaims.delete && scopes.includes(options.acceptedClaims.delete)) {
                resourceList.push(`arn:aws:execute-api:${region}:*:*/DELETE/*`);
            }
            // websocket connection scope
            if (options.acceptedClaims.connect && scopes.includes(options.acceptedClaims.connect)) {
                resourceList.push(`arn:aws:execute-api:${region}:*:*/${options.stage}/$connect`);
            }
            const principal = decodedToken.payload.sub;
            const policy = {
                principalId: principal,
                policyDocument: {
                    Version: '2012-10-17',
                    Statement: [
                        {
                            Effect: 'Allow',
                            Action: ['execute-api:Invoke'],
                            Resource: resourceList,
                        },
                    ],
                },
                context: {
                    principal,
                    createdAt: new Date(),
                },
            };
            logger_1.Logger.debug(`authorized for ${scopes.join(', ')} access`);
            return policy;
        }
        catch (err) {
            throw new Unauthorized_1.Unauthorized({ internalError: err });
        }
    }
}
exports.AuthorizerAuth0 = AuthorizerAuth0;
//# sourceMappingURL=AuthorizerAuth0.js.map